import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, ArrowUpRight } from "lucide-react";
import Logo from "./Logo";

const SECTION_NAV = [
  { label: "Founder", to: "/founder", hash: false },
  { label: "Eduzy", to: "/eduzy", hash: false },
  { label: "Privacy", to: "/privacy", hash: false },
];

export default function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname, location.hash]);

  return (
    <header
      data-testid="site-header"
      className={`sticky top-0 z-40 w-full transition-all duration-300 ${
        scrolled
          ? "border-b border-slate-200/80 bg-[#FAFAFA]/85 backdrop-blur-md"
          : "border-b border-transparent bg-[#FAFAFA]"
      }`}
    >
      <div className="ct-container flex h-20 items-center justify-between md:h-24">
        <Link to="/" data-testid="header-logo" className="flex items-center gap-2.5">
          <Logo />
        </Link>

        <nav className="hidden items-center gap-7 lg:flex" data-testid="primary-nav">
          {SECTION_NAV.map((item) =>
            item.hash ? (
              <a
                key={item.label}
                href={item.to}
                data-testid={`nav-${item.label.toLowerCase()}`}
                className="ct-link-underline text-sm font-medium text-[#0F172A] hover:text-neutral-700"
              >
                {item.label}
              </a>
            ) : (
              <Link
                key={item.label}
                to={item.to}
                data-testid={`nav-${item.label.toLowerCase()}`}
                className={`ct-link-underline text-sm font-medium hover:text-neutral-700 ${
                  location.pathname === item.to ? "text-neutral-700" : "text-[#0F172A]"
                }`}
              >
                {item.label}
              </Link>
            )
          )}
        </nav>

        <div className="hidden lg:block">
          <a
            href="/contact"
            data-testid="header-contact-cta"
            className="inline-flex items-center gap-1.5 rounded-full bg-[#0A0A0A] px-5 py-2.5 text-sm font-semibold text-white transition-all duration-300 hover:gap-2.5 hover:bg-[#262626]"
          >
            Contact
            <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5" />
          </a>
        </div>

        <button
          onClick={() => setOpen((s) => !s)}
          aria-label="Toggle menu"
          data-testid="mobile-menu-toggle"
          className="inline-flex items-center justify-center rounded-md p-2 text-[#0F172A] lg:hidden"
        >
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      {open && (
        <div className="border-t border-slate-200 bg-[#FAFAFA] lg:hidden" data-testid="mobile-menu">
          <div className="ct-container flex flex-col gap-1 py-4">
            {SECTION_NAV.map((item) =>
              item.hash ? (
                <a
                  key={item.label}
                  href={item.to}
                  data-testid={`mobile-nav-${item.label.toLowerCase()}`}
                  className="rounded-md px-3 py-3 text-base font-medium text-[#0F172A] hover:bg-neutral-50 hover:text-neutral-700"
                >
                  {item.label}
                </a>
              ) : (
                <Link
                  key={item.label}
                  to={item.to}
                  data-testid={`mobile-nav-${item.label.toLowerCase()}`}
                  className="rounded-md px-3 py-3 text-base font-medium text-[#0F172A] hover:bg-neutral-50 hover:text-neutral-700"
                >
                  {item.label}
                </Link>
              )
            )}
            <a
              href="/contact"
              data-testid="mobile-contact-cta"
              className="mt-2 inline-flex items-center justify-center gap-1.5 rounded-full bg-[#0A0A0A] px-5 py-3 text-sm font-semibold text-white"
            >
              Contact <ArrowUpRight className="h-4 w-4" />
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
